import { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, ChevronRight, Trash2, Minus, Plus, Globe, Heart, User, LogOut, Check, ExternalLink, Send } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/lib/store";
import { getImageUrl, checkPromo, createOrder, customerVerifyStart, customerVerifyStatus, customerMe, customerLogout, getCustomerToken, setCustomerToken, customerRegister, customerLogin, customerUnreadCount } from "@/lib/api";
import { toast } from "sonner";

const CURRENCIES = ["RUB", "USD", "EUR", "BYN"];

export default function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [comment, setComment] = useState("");
  const [orderLoading, setOrderLoading] = useState(false);

  // Telegram customer auth state
  const [customer, setCustomer] = useState(null);  // {id, tg_username, tg_first_name, orders_count, total_spent, ...}
  const [tgVerifying, setTgVerifying] = useState(false);
  const [tgVerifyData, setTgVerifyData] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [authMode, setAuthMode] = useState(null);  // null | 'login' | 'register'
  const [authForm, setAuthForm] = useState({ username: "", password: "", display_name: "", contact: "" });
  const [authBusy, setAuthBusy] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [authPanelOpen, setAuthPanelOpen] = useState(false);  // Profile auth dialog from header
  const pollRef = useRef(null);

  const { cart, cartTotal, cartCount, removeFromCart, updateQuantity, clearCart, formatPrice, setCurrency, currency, language, setLanguage, t, categories, getCategoryLabel, storeName, storeDescription, cartOpen, setCartOpen, favorites, favoritesCount, favoritesOpen, setFavoritesOpen, removeFavorite } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  // On mount: try to re-hydrate customer session from localStorage
  useEffect(() => {
    if (!getCustomerToken()) return;
    customerMe()
      .then((c) => setCustomer(c))
      .catch(() => { customerLogout(); setCustomer(null); });
  }, []);

  // Listen for auth changes triggered from other parts of the app (e.g. AccountPage logout)
  useEffect(() => {
    const onAuthChange = () => {
      const token = getCustomerToken();
      if (!token) {
        setCustomer(null);
        setShowManualForm(false);
        setAuthMode(null);
        return;
      }
      customerMe()
        .then((c) => setCustomer(c))
        .catch(() => { customerLogout(); setCustomer(null); });
    };
    window.addEventListener("customer-auth-changed", onAuthChange);
    window.addEventListener("storage", onAuthChange);  // cross-tab support
    return () => {
      window.removeEventListener("customer-auth-changed", onAuthChange);
      window.removeEventListener("storage", onAuthChange);
    };
  }, []);

  // Poll unread messages count every 30 s when logged in
  useEffect(() => {
    if (!customer) { setUnreadMessages(0); return; }
    let alive = true;
    const tick = () => customerUnreadCount()
      .then((r) => { if (alive) setUnreadMessages(r.unread || 0); })
      .catch(() => {});
    tick();
    const id = setInterval(tick, 30000);
    // Immediate refresh when account page reports it just read messages
    const onRead = () => { if (alive) tick(); };
    window.addEventListener("customer-messages-read", onRead);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("customer-messages-read", onRead);
    };
  }, [customer]);

  // Cleanup polling when modal closes / unmounts
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleStartTgVerify = async () => {
    setTgVerifying(true);
    try {
      const r = await customerVerifyStart();
      if (!r.bot_link) {
        toast.error("Бот не настроен");
        setTgVerifying(false);
        return;
      }
      setTgVerifyData(r);
      // Open Telegram bot in a new tab
      window.open(r.bot_link, "_blank", "noopener");
      // Poll for verification status every 2s
      pollRef.current = setInterval(async () => {
        try {
          const s = await customerVerifyStatus(r.token);
          if (s.status === "verified" && s.token && s.customer) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setCustomerToken(s.token);
            setCustomer(s.customer);
            setTgVerifying(false);
            setTgVerifyData(null);
            setAuthPanelOpen(false);
            const greet = s.customer.tg_first_name || ("@" + (s.customer.tg_username || ""));
            toast.success(`✓ Подтверждено, ${greet}!`);
          } else if (s.status === "expired") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setTgVerifying(false);
            setTgVerifyData(null);
            toast.error("Срок подтверждения истёк. Попробуйте снова.");
          }
        } catch (e) { console.error(e); }
      }, 2000);
    } catch (e) {
      console.error(e);
      toast.error("Не удалось запустить подтверждение");
      setTgVerifying(false);
    }
  };

  const handleCancelTgVerify = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setTgVerifying(false);
    setTgVerifyData(null);
  };

  const handleCustomerLogout = () => {
    customerLogout();
    setCustomer(null);
    setShowManualForm(false);
    setAuthMode(null);
    toast.success("Вы вышли из аккаунта");
  };

  const handleAuthSubmit = async () => {
    if (!authForm.username.trim() || !authForm.password) {
      toast.error("Введите логин и пароль");
      return;
    }
    if (authMode === "register") {
      if (authForm.username.length < 3) { toast.error("Логин минимум 3 символа"); return; }
      if (authForm.password.length < 6) { toast.error("Пароль минимум 6 символов"); return; }
      if (!authForm.contact.trim() || authForm.contact.trim().length < 4) {
        toast.error("Укажите контакт для связи (телефон, Telegram и т.п.)");
        return;
      }
    }
    setAuthBusy(true);
    try {
      const r = authMode === "register"
        ? await customerRegister({ username: authForm.username, password: authForm.password, display_name: authForm.display_name, contact: authForm.contact })
        : await customerLogin({ username: authForm.username, password: authForm.password });
      setCustomerToken(r.token);
      setCustomer(r.customer);
      setAuthMode(null);
      setAuthForm({ username: "", password: "", display_name: "", contact: "" });
      setAuthPanelOpen(false);
      toast.success(authMode === "register" ? "Аккаунт создан!" : "Вход выполнен");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка");
    } finally {
      setAuthBusy(false);
    }
  };

  const handleCheckPromo = async () => {
    if (!promoCode.trim()) return;
    try {
      const result = await checkPromo(promoCode);
      setPromoDiscount(result);
      toast.success(t("promoApplied"));
    } catch {
      toast.error(t("promoNotFound"));
      setPromoDiscount(null);
    }
  };

  const discount = promoDiscount
    ? promoDiscount.discount_percent
      ? Math.round(cartTotal * promoDiscount.discount_percent / 100)
      : promoDiscount.discount_amount || 0
    : 0;

  const handleOrder = async () => {
    // If logged in via Telegram, skip name/contact requirement
    if (!customer) {
      if (!customerName.trim() || !customerContact.trim()) {
        toast.error(t("fillNameContact"));
        return;
      }
    }
    setOrderLoading(true);
    try {
      await createOrder({
        items: cart.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        promo_code: promoCode || undefined,
        customer_name: customer ? (customer.tg_first_name || customer.tg_username || "") : customerName,
        customer_contact: customer ? ("@" + (customer.tg_username || customer.tg_first_name || "")) : customerContact,
        comment,
        customer_token: getCustomerToken() || undefined,
      });
      toast.success(t("orderSuccess"));
      clearCart();
      setCartOpen(false);
      setCheckoutMode(false);
      setPromoCode("");
      setPromoDiscount(null);
      setCustomerName("");
      setCustomerContact("");
      setComment("");
      setShowManualForm(false);
      // Refresh customer stats (orders_count, total_spent)
      if (customer) customerMe().then(setCustomer).catch(() => {});
    } catch {
      toast.error(t("orderError"));
    } finally {
      setOrderLoading(false);
    }
  };

  // Build navigation categories from dynamic data
  const navCategories = categories;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="app-layout">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full bg-black/90 backdrop-blur-sm border-b border-zinc-800" data-testid="main-header">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center px-4 h-14">
          <div className="flex items-center justify-self-start">
            <button onClick={() => setMenuOpen(true)} className="p-2 hover:bg-zinc-900 transition-colors" data-testid="menu-toggle">
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
          <Link to="/" className="font-display text-lg md:text-xl font-bold tracking-wider justify-self-center" data-testid="logo-link">
            {storeName}
          </Link>
          <div className="flex items-center gap-1 justify-self-end">
            {customer ? (
              <Link to="/account" className="p-2 hover:bg-zinc-900 transition-colors relative group" data-testid="header-account-link" title={customer.display_name || customer.tg_first_name || customer.username}>
                <User className="w-5 h-5" strokeWidth={1.5} />
                {unreadMessages > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-emerald-500 text-black text-[10px] font-mono font-bold flex items-center justify-center rounded-full" data-testid="header-unread-badge">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </Link>
            ) : (
              <button onClick={() => setAuthPanelOpen(true)} className="p-2 hover:bg-zinc-900 transition-colors" data-testid="header-login-btn" title="Войти в аккаунт">
                <User className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}
            <button onClick={() => setFavoritesOpen(true)} className="p-2 hover:bg-zinc-900 transition-colors relative" data-testid="favorites-toggle">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
              {favoritesCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white text-black text-[10px] font-mono font-bold flex items-center justify-center">
                  {favoritesCount}
                </span>
              )}
            </button>
            <button onClick={() => setCartOpen(true)} className="p-2 hover:bg-zinc-900 transition-colors relative" data-testid="cart-toggle">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white text-black text-[10px] font-mono font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Menu Sheet */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="bg-black border-zinc-800 w-[280px] p-0 flex flex-col">
          <SheetHeader className="p-6 border-b border-zinc-800">
            <SheetTitle className="font-display text-xl text-white tracking-wider">{storeName}</SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs font-mono">{t("menu")}</SheetDescription>
          </SheetHeader>
          <nav className="flex-1 flex flex-col overflow-y-auto overscroll-contain pb-8" style={{ WebkitOverflowScrolling: "touch" }}>
            <Link to="/" onClick={() => setMenuOpen(false)}
              className="px-6 py-4 border-b border-zinc-800/50 text-sm font-display tracking-wider hover:bg-zinc-900 transition-colors flex items-center justify-between"
              data-testid="nav-home">
              {t("home")}
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </Link>
            {navCategories.map((cat) => (
              <Link key={cat.slug} to={`/catalog/${cat.slug}`}
                onClick={() => setMenuOpen(false)}
                className="px-6 py-4 border-b border-zinc-800/50 text-sm font-display tracking-wider hover:bg-zinc-900 transition-colors flex items-center justify-between"
                data-testid={`nav-${cat.slug}`}>
                {getCategoryLabel(cat.slug)}
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </Link>
            ))}

            {/* Favorites link */}
            <Link to="/favorites" onClick={() => setMenuOpen(false)}
              className="px-6 py-4 border-b border-zinc-800/50 text-sm font-display tracking-wider hover:bg-zinc-900 transition-colors flex items-center gap-2"
              data-testid="nav-favorites">
              <Heart className="w-4 h-4" /> {t("favorites")}
            </Link>

            {/* Currency selector */}
            <div className="px-6 py-4 border-b border-zinc-800/50">
              <p className="text-xs font-mono text-zinc-500 mb-2">{t("currency")}</p>
              <div className="flex flex-wrap gap-2">
                {CURRENCIES.map((c) => (
                  <button key={c} onClick={() => setCurrency(c)}
                    className={`px-3 py-1 text-xs font-mono border transition-colors ${currency === c ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
                    data-testid={`currency-${c.toLowerCase()}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Language selector */}
            <div className="px-6 py-4 border-b border-zinc-800/50">
              <p className="text-xs font-mono text-zinc-500 mb-2 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> {t("language")}
              </p>
              <div className="flex gap-2">
                {["RU", "EN"].map((lang) => (
                  <button key={lang} onClick={() => setLanguage(lang)}
                    className={`px-3 py-1 text-xs font-mono border transition-colors ${language === lang ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}
                    data-testid={`lang-${lang.toLowerCase()}`}>
                    {lang}
                  </button>
                ))}
              </div>
            </div>
            {/* Info pages */}
            <Link to="/rules" onClick={() => setMenuOpen(false)}
              className="px-6 py-4 border-b border-zinc-800/50 text-sm font-display tracking-wider hover:bg-zinc-900 transition-colors flex items-center justify-between"
              data-testid="nav-rules">
              {t("rules")}
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </Link>
            <Link to="/about" onClick={() => setMenuOpen(false)}
              className="px-6 py-4 border-b border-zinc-800/50 text-sm font-display tracking-wider hover:bg-zinc-900 transition-colors flex items-center justify-between"
              data-testid="nav-about">
              {t("aboutUs")}
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </Link>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Favorites Sheet */}
      <Sheet open={favoritesOpen} onOpenChange={setFavoritesOpen}>
        <SheetContent side="right" className="bg-black border-zinc-800 w-full sm:w-[420px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-zinc-800 shrink-0">
            <SheetTitle className="font-display text-lg text-white tracking-wider flex items-center gap-2">
              <Heart className="w-5 h-5" /> {t("favorites")}
            </SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs font-mono">{favoritesCount} {t("items")}</SheetDescription>
          </SheetHeader>

          {favorites.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 gap-4">
              <Heart className="w-16 h-16 text-zinc-700" strokeWidth={1} />
              <p className="text-sm">{t("favoritesEmpty")}</p>
              <button onClick={() => { setFavoritesOpen(false); navigate("/catalog"); }}
                className="px-6 py-3 bg-white text-black text-xs font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors">
                {t("viewCatalog")}
              </button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {favorites.map((item) => (
                <Link key={item.id} to={`/product/${item.id}`} onClick={() => setFavoritesOpen(false)}
                  className="flex gap-3 p-4 border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors" data-testid={`fav-item-${item.id}`}>
                  <div className="w-20 h-24 bg-zinc-900 shrink-0 overflow-hidden">
                    {item.images?.[0] && <img src={getImageUrl(item.images[0], 200)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-display tracking-wide truncate">{item.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {item.old_price && item.old_price > item.price && (
                        <span className="text-[10px] font-mono text-zinc-500 line-through">{formatPrice(item.old_price)}</span>
                      )}
                      <span className="text-xs font-mono font-bold">{formatPrice(item.price)}</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFavorite(item.id); }}
                    className="p-1 hover:bg-zinc-800 self-start" data-testid={`fav-remove-${item.id}`}>
                    <Trash2 className="w-4 h-4 text-zinc-500" />
                  </button>
                </Link>
              ))}
            </div>
          )}

          {favorites.length > 0 && (
            <div className="shrink-0 border-t border-zinc-800 p-4">
              <button onClick={() => { setFavoritesOpen(false); navigate("/favorites"); }}
                className="w-full h-12 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
                data-testid="view-all-favorites-btn">
                {t("viewAll")}
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Cart Sheet */}
      <Sheet open={cartOpen} onOpenChange={(open) => { setCartOpen(open); if (!open) setCheckoutMode(false); }}>
        <SheetContent side="right" className="bg-black border-zinc-800 w-full sm:w-[420px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-zinc-800 shrink-0">
            <SheetTitle className="font-display text-lg text-white tracking-wider">
              {checkoutMode ? t("checkout") : t("cart")}
            </SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs font-mono">{cartCount} {t("items")}</SheetDescription>
          </SheetHeader>

          {cart.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">{t("cartEmpty")}</div>
          ) : checkoutMode ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="checkout-form">
              {customer ? (
                /* Logged in via Telegram */
                <div className="p-3 bg-emerald-950/30 border border-emerald-900/50" data-testid="customer-logged-in">
                  <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 shrink-0 flex items-center justify-center font-display text-sm font-bold">
                      {(customer.tg_first_name || customer.tg_username || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-display flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        {customer.tg_first_name || "Клиент"}
                        {customer.tg_username && (
                          <span className="text-zinc-400 font-mono text-xs">@{customer.tg_username}</span>
                        )}
                      </p>
                      <p className="text-[11px] font-mono text-zinc-500 mt-0.5">
                        Подтверждено через Telegram
                        {customer.orders_count > 0 && (
                          <span className="text-amber-400 ml-2">⭐ {customer.orders_count} заказов</span>
                        )}
                      </p>
                    </div>
                    <button onClick={handleCustomerLogout}
                      className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors" title="Выйти"
                      data-testid="customer-logout-btn">
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : tgVerifying ? (
                <div className="p-4 bg-zinc-900 border border-sky-900/50 text-center space-y-3" data-testid="tg-verify-pending">
                  <div className="w-10 h-10 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                  <p className="text-sm text-white">Откройте Telegram и подтвердите...</p>
                  <p className="text-[11px] font-mono text-zinc-500">
                    Если Telegram не открылся автоматически:
                  </p>
                  {tgVerifyData?.bot_link && (
                    <a href={tgVerifyData.bot_link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-mono text-sky-400 hover:text-sky-300 underline"
                      data-testid="tg-verify-manual-link">
                      Открыть @{tgVerifyData.bot_username} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <button onClick={handleCancelTgVerify}
                    className="block mx-auto text-[11px] font-mono text-zinc-500 hover:text-white underline" data-testid="tg-verify-cancel">
                    Отменить
                  </button>
                </div>
              ) : authMode ? (
                /* Login or Register form */
                <div className="space-y-3" data-testid="auth-form">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-display tracking-wider">
                      {authMode === "register" ? "РЕГИСТРАЦИЯ" : "ВХОД"}
                    </h3>
                    <button onClick={() => setAuthMode(null)} className="text-zinc-500 hover:text-white" data-testid="auth-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input value={authForm.username}
                    onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-white text-sm focus:outline-none focus:border-zinc-600"
                    placeholder="Логин (3-32 символа)" autoComplete="username"
                    data-testid="auth-username" />
                  {authMode === "register" && (
                    <input value={authForm.display_name}
                      onChange={(e) => setAuthForm({ ...authForm, display_name: e.target.value })}
                      className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-white text-sm focus:outline-none focus:border-zinc-600"
                      placeholder="Имя (как к вам обращаться)"
                      data-testid="auth-display-name" />
                  )}
                  {authMode === "register" && (
                    <>
                      <input value={authForm.contact}
                        onChange={(e) => setAuthForm({ ...authForm, contact: e.target.value })}
                        className="w-full bg-zinc-900 border border-amber-500/40 h-11 px-3 text-white text-sm focus:outline-none focus:border-amber-400"
                        placeholder="Контакт для связи * (+7..., @telegram)"
                        data-testid="auth-contact" />
                      <p className="text-[10px] font-mono text-amber-400/80 -mt-1 leading-relaxed">
                        ⚠ Будьте внимательны — без верного контакта мы не сможем связаться с вами по заказу.
                      </p>
                    </>
                  )}
                  <input type="password" value={authForm.password}
                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-white text-sm focus:outline-none focus:border-zinc-600"
                    placeholder={authMode === "register" ? "Пароль (мин. 6)" : "Пароль"} autoComplete={authMode === "register" ? "new-password" : "current-password"}
                    onKeyDown={(e) => e.key === "Enter" && handleAuthSubmit()}
                    data-testid="auth-password" />
                  <button onClick={handleAuthSubmit} disabled={authBusy}
                    className="w-full h-11 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
                    data-testid="auth-submit">
                    {authBusy ? "..." : (authMode === "register" ? "СОЗДАТЬ АККАУНТ" : "ВОЙТИ")}
                  </button>
                  <p className="text-[11px] font-mono text-zinc-500 text-center">
                    {authMode === "register" ? "Уже есть аккаунт? " : "Нет аккаунта? "}
                    <button onClick={() => setAuthMode(authMode === "register" ? "login" : "register")}
                      className="text-white underline hover:text-sky-400" data-testid="auth-toggle">
                      {authMode === "register" ? "Войти" : "Создать"}
                    </button>
                  </p>
                  {authMode === "login" && (
                    <Link to="/support" onClick={() => setCartOpen(false)}
                      className="block text-center text-[11px] font-mono text-zinc-500 hover:text-white underline"
                      data-testid="auth-forgot-link">
                      Забыли пароль? Связаться с нами
                    </Link>
                  )}
                </div>
              ) : (
                /* Not logged in: offer TG verify + login/register + fallback */
                <div className="space-y-3">
                  <button onClick={handleStartTgVerify}
                    className="w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-display text-sm font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                    data-testid="tg-verify-btn">
                    <Check className="w-4 h-4" />
                    Подтвердить через Telegram
                  </button>
                  <p className="text-[11px] font-mono text-zinc-500 leading-relaxed text-center">
                    Один клик. Мы свяжемся с вами после оформления.
                  </p>
                  <div className="flex items-center gap-3 pt-1">
                    <div className="flex-1 h-px bg-zinc-800" />
                    <span className="text-[10px] font-mono text-zinc-600 uppercase">или</span>
                    <div className="flex-1 h-px bg-zinc-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setAuthMode("login")}
                      className="h-11 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-display font-bold uppercase tracking-wider transition-colors"
                      data-testid="open-login">
                      ВОЙТИ
                    </button>
                    <button onClick={() => setAuthMode("register")}
                      className="h-11 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-display font-bold uppercase tracking-wider transition-colors"
                      data-testid="open-register">
                      РЕГИСТРАЦИЯ
                    </button>
                  </div>
                  {!showManualForm ? (
                    <button onClick={() => setShowManualForm(true)}
                      className="block mx-auto text-[11px] font-mono text-zinc-500 hover:text-white underline" data-testid="show-manual-form">
                      или продолжить без аккаунта
                    </button>
                  ) : (
                    <div className="space-y-3 pt-2 border-t border-zinc-800/60">
                      <p className="text-[11px] font-mono text-amber-300/80 leading-relaxed text-center">
                        ⚠ Будьте очень внимательны
                      </p>
                      <div>
                        <label className="text-xs font-mono text-zinc-400 uppercase tracking-wider">{t("name")}</label>
                        <input value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                          className="w-full mt-1 bg-zinc-900 border border-zinc-800 h-12 px-3 text-white text-sm focus:outline-none focus:border-zinc-600"
                          placeholder={t("namePlaceholder")} data-testid="checkout-name" />
                      </div>
                      <div>
                        <label className="text-xs font-mono text-zinc-400 uppercase tracking-wider">{t("contact")}</label>
                        <input value={customerContact} onChange={(e) => setCustomerContact(e.target.value)}
                          className="w-full mt-1 bg-zinc-900 border border-zinc-800 h-12 px-3 text-white text-sm focus:outline-none focus:border-zinc-600"
                          placeholder={t("contactPlaceholder")} data-testid="checkout-contact" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="p-3 border border-zinc-800 bg-zinc-900/40" data-testid="checkout-payment-note">
                <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
                  💳 Оплата — после подтверждения заказа менеджером в Telegram.
                </p>
              </div>

              <div>
                <label className="text-xs font-mono text-zinc-400 uppercase tracking-wider">{t("commentLabel")}</label>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 p-3 text-white text-sm focus:outline-none focus:border-zinc-600 min-h-[80px] resize-none"
                  placeholder={t("commentPlaceholder")} data-testid="checkout-comment" />
                {!customer?.tg_username && (
                  <p className="mt-1.5 text-[10px] font-mono text-amber-400/80 leading-relaxed" data-testid="checkout-comment-hint">
                    ⚠ Если у вас скрыт @username и не указан номер — пожалуйста, оставьте здесь способ связи (Telegram, WhatsApp, Instagram, ВК), иначе мы не сможем уточнить детали заказа.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {cart.map((item, idx) => (
                <div key={idx} className="flex gap-3 p-4 border-b border-zinc-800/50" data-testid={`cart-item-${idx}`}>
                  <div className="w-20 h-20 bg-zinc-900 shrink-0 overflow-hidden">
                    {item.image && <img src={getImageUrl(item.image, 200)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-display tracking-wide truncate">{item.title}</p>
                    <p className="text-xs font-mono text-zinc-400 mt-0.5">{formatPrice(item.price)}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{t("size")}: {item.size}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQuantity(idx, item.quantity - 1)} className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:bg-zinc-800" data-testid={`cart-minus-${idx}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-mono w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(idx, item.quantity + 1)} className="w-6 h-6 border border-zinc-700 flex items-center justify-center hover:bg-zinc-800" data-testid={`cart-plus-${idx}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(idx)} className="p-1 hover:bg-zinc-800 self-start" data-testid={`cart-remove-${idx}`}>
                    <Trash2 className="w-4 h-4 text-zinc-500" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {cart.length > 0 && (
            <div className="shrink-0 border-t border-zinc-800 p-4 space-y-3">
              {!checkoutMode && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{t("subtotal")}</span>
                    <span className="font-mono">{formatPrice(cartTotal)}</span>
                  </div>
                  <div>
                    <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">{t("promoCode")}</p>
                    <div className="flex gap-2">
                      <input value={promoCode} onChange={(e) => setPromoCode(e.target.value)}
                        className="flex-1 bg-zinc-900 border border-zinc-800 h-10 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
                        placeholder={t("promoPlaceholder")} data-testid="promo-input" />
                      <button onClick={handleCheckPromo}
                        className="px-4 h-10 bg-zinc-800 text-xs font-mono uppercase hover:bg-zinc-700 transition-colors" data-testid="promo-apply">
                        OK
                      </button>
                    </div>
                    {promoDiscount && (
                      <p className="text-xs text-green-400 mt-1 font-mono">-{formatPrice(discount)}</p>
                    )}
                  </div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-800">
                    <span>{t("total")}</span>
                    <span className="font-mono">{formatPrice(cartTotal - discount)}</span>
                  </div>
                </>
              )}
              <button onClick={checkoutMode ? handleOrder : () => setCheckoutMode(true)}
                disabled={orderLoading || (checkoutMode && tgVerifying)}
                className="w-full h-12 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
                data-testid="checkout-btn">
                {orderLoading ? "..." : checkoutMode ? (customer ? "КУПИТЬ В 1 КЛИК" : t("confirmOrder")) : t("buy")}
              </button>
              {checkoutMode && (
                <button onClick={() => setCheckoutMode(false)}
                  className="w-full h-10 border border-zinc-700 text-zinc-400 text-xs font-mono uppercase hover:bg-zinc-900 transition-colors"
                  data-testid="back-to-cart-btn">
                  {t("backToCart")}
                </button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-56px)]">
        <Outlet />
      </main>

      {/* Profile auth dialog — shown when guest clicks the user icon */}
      {authPanelOpen && !customer && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setAuthPanelOpen(false); setAuthMode(null); }}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="auth-panel-modal">
            <div className="border-b border-zinc-800 p-4 flex items-center justify-between">
              <h3 className="font-display text-base tracking-wider">ЛИЧНЫЙ КАБИНЕТ</h3>
              <button onClick={() => { setAuthPanelOpen(false); setAuthMode(null); }} className="p-1 hover:bg-zinc-800" data-testid="auth-panel-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!authMode && !tgVerifying && (
                <>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Войдите или создайте аккаунт, чтобы видеть статус заказов, общаться с поддержкой и получать персональные скидки.
                  </p>
                  <button onClick={handleStartTgVerify}
                    className="w-full flex items-center justify-center gap-2 bg-[#229ED9] hover:bg-[#1c87b9] text-white font-mono uppercase text-xs tracking-wider py-3 transition-colors"
                    data-testid="auth-panel-tg-btn">
                    <Send className="w-4 h-4" /> Войти через Telegram
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setAuthMode("login")}
                      className="border border-zinc-700 hover:border-white hover:bg-zinc-900 text-white font-mono uppercase text-xs tracking-wider py-2.5 transition-colors"
                      data-testid="auth-panel-login-btn">
                      Войти
                    </button>
                    <button onClick={() => setAuthMode("register")}
                      className="border border-zinc-700 hover:border-white hover:bg-zinc-900 text-white font-mono uppercase text-xs tracking-wider py-2.5 transition-colors"
                      data-testid="auth-panel-register-btn">
                      Регистрация
                    </button>
                  </div>
                </>
              )}
              {tgVerifying && (
                <div className="space-y-3">
                  <p className="text-sm text-zinc-400">Откройте Telegram, нажмите Start в боте и подтвердите вход.</p>
                  <a href={tgVerifyData?.bot_link} target="_blank" rel="noopener noreferrer"
                    className="block w-full text-center bg-[#229ED9] hover:bg-[#1c87b9] text-white font-mono uppercase text-xs tracking-wider py-3 transition-colors">
                    Открыть бота
                  </a>
                  <button onClick={handleCancelTgVerify}
                    className="w-full border border-zinc-700 hover:border-white text-zinc-300 font-mono uppercase text-xs tracking-wider py-2.5 transition-colors"
                    data-testid="auth-panel-tg-cancel">
                    Отменить
                  </button>
                </div>
              )}
              {authMode && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Логин</label>
                    <input value={authForm.username} onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                      placeholder="Минимум 3 символа"
                      className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-zinc-600"
                      data-testid="auth-panel-username" />
                  </div>
                  {authMode === "register" && (
                    <div>
                      <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Имя (как обращаться)</label>
                      <input value={authForm.display_name} onChange={(e) => setAuthForm({ ...authForm, display_name: e.target.value })}
                        placeholder="Необязательно"
                        className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-zinc-600"
                        data-testid="auth-panel-displayname" />
                    </div>
                  )}
                  {authMode === "register" && (
                    <div>
                      <label className="text-[11px] font-mono text-amber-400 uppercase block mb-1">Контакт для связи *</label>
                      <input value={authForm.contact} onChange={(e) => setAuthForm({ ...authForm, contact: e.target.value })}
                        placeholder="+7 999 123-45-67 или @telegram"
                        className="w-full bg-zinc-900 border border-amber-500/40 px-3 py-2 text-sm text-white outline-none focus:border-amber-400"
                        data-testid="auth-panel-contact" />
                      <p className="mt-1.5 text-[10px] font-mono text-amber-400/80 leading-relaxed">
                        ⚠ Будьте внимательны — без верного контакта мы не сможем связаться с вами по заказу. Подойдёт номер телефона, Telegram, WhatsApp, Instagram, ВК.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Пароль</label>
                    <input type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                      placeholder={authMode === "register" ? "Минимум 6 символов" : ""}
                      className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-zinc-600"
                      onKeyDown={(e) => { if (e.key === "Enter") handleAuthSubmit(); }}
                      data-testid="auth-panel-password" />
                  </div>
                  <button onClick={handleAuthSubmit} disabled={authBusy}
                    className="w-full bg-white hover:bg-zinc-200 text-black font-mono uppercase text-xs tracking-wider py-3 transition-colors disabled:opacity-50"
                    data-testid="auth-panel-submit">
                    {authBusy ? "..." : (authMode === "register" ? "Создать аккаунт" : "Войти")}
                  </button>
                  <button onClick={() => setAuthMode(null)}
                    className="w-full text-zinc-500 hover:text-white font-mono uppercase text-[11px] tracking-wider py-2 transition-colors"
                    data-testid="auth-panel-back">
                    ← Назад
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
            {/* Brand */}
            <div className="md:col-span-1">
              <p className="font-display text-lg tracking-wider text-white">{storeName}</p>
              <p className="text-xs text-zinc-500 mt-2 leading-relaxed max-w-[240px]">{storeDescription}</p>
            </div>

            {/* Catalog */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-3">{t("shopCol")}</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/catalog" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-catalog">{t("catalog")}</Link>
                {navCategories.slice(0, 4).map((cat) => (
                  <Link key={cat.slug} to={`/catalog/${cat.slug}`} className="text-zinc-400 hover:text-white transition-colors">
                    {getCategoryLabel(cat.slug)}
                  </Link>
                ))}
              </div>
            </div>

            {/* For buyers */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-3">{t("forBuyers")}</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/rules" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-rules">{t("rules")}</Link>
                <Link to="/support" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-support">{t("askQuestion")}</Link>
              </div>
            </div>

            {/* Company */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-3">{t("companyCol")}</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/about" className="text-zinc-400 hover:text-white transition-colors" data-testid="footer-about">{t("aboutUs")}</Link>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-zinc-800/70">
            <p className="text-[11px] text-zinc-700 font-mono">© {new Date().getFullYear()} {storeName}.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}